/**
 * Native HTML5 drag-and-drop for the Stickyboard, mirroring the workflow
 * editor's pattern (serialize on `dataTransfer`, parse on drop) — see
 * `features/agents/components/workflow-agent/hooks/useWorkflowDnd.tsx`. No DnD
 * library. A drop writes a USER OVERRIDE via the existing `PATCH /stickies/{id}`
 * (optimistic), which wins over the Steward's curation, so the move sticks.
 *
 * One `useStickyboardDndValue()` instance is provided by the board and shared by
 * the lanes / cards / piles through `StickyboardDndContext`, so a single set of
 * mutations backs every drop zone (the drag payload travels on the native
 * `dataTransfer`, not React state).
 */
import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { effectiveSectionId, effectiveStackId, type StickyCard } from '@frontend/types/stickyboard';

import { useStewardNotifier } from './useStewardNotifier';
import {
  useCreateStack,
  usePatchSticky,
  useSections,
  useStacks,
  useUpdateStack,
} from './useStickyboard';
import { useSuggestStackName } from './useSuggestStackName';

const MIME = 'application/json';
const HIGHLIGHT = '#6366f1';

type CardDrag = {
  kind: 'card';
  stickyId: string;
  fromSection: string;
  fromStack: string;
  title: string;
};
type StackDrag = { kind: 'stack'; stackId: string; section: string; memberIds: string[] };
type DragPayload = CardDrag | StackDrag;

type DragHandler = (e: React.DragEvent) => void;

export type StickyboardDnd = {
  /** onDragStart for a single card. */
  startCardDrag: (card: StickyCard) => DragHandler;
  /** onDragStart for a whole (collapsed) stack pile. */
  startStackDrag: (stackId: string, section: string, members: StickyCard[]) => DragHandler;
  /** onDragOver for any drop zone (enables the drop + draws the highlight). */
  allowDrop: DragHandler;
  /** onDragLeave for any drop zone (clears the highlight). */
  leaveDrop: DragHandler;
  /** onDrop for a section lane (`''` = the Unsorted lane). */
  dropOnSection: (sectionId: string) => DragHandler;
  /** onDrop for a stack pile (drop a card on it to join). */
  dropOnStack: (stackId: string, section: string) => DragHandler;
  /** onDrop for a single card (drop a card on it to form/join a stack). */
  dropOnCard: (target: StickyCard) => DragHandler;
  /** onDragEnd for any draggable — clears any lingering drop-zone highlight. */
  endDrag: DragHandler;
};

const NOOP: StickyboardDnd = {
  startCardDrag: () => () => {},
  startStackDrag: () => () => {},
  allowDrop: () => {},
  leaveDrop: () => {},
  dropOnSection: () => () => {},
  dropOnStack: () => () => {},
  dropOnCard: () => () => {},
  endDrag: () => {},
};

const StickyboardDndContext = createContext<StickyboardDnd>(NOOP);

export function useStickyboardDnd(): StickyboardDnd {
  return useContext(StickyboardDndContext);
}

// Drop-zone highlight is drawn with `outline` (no layout impact, so it never
// reflows the dragged card and aborts the native drag). Each highlighted element
// is tagged so we can clear ALL of them on drop / drag-end — drop zones nest
// (a card inside the lane grid), so a single drag can highlight several.
const HIGHLIGHT_ATTR = 'data-sb-dropzone-active';

function highlight(el: HTMLElement) {
  el.style.outline = `2px dashed ${HIGHLIGHT}`;
  el.style.outlineOffset = '2px';
  el.setAttribute(HIGHLIGHT_ATTR, '');
}
function unhighlight(el: HTMLElement) {
  el.style.outline = '';
  el.style.outlineOffset = '';
  el.removeAttribute(HIGHLIGHT_ATTR);
}
function clearHighlights() {
  document.querySelectorAll(`[${HIGHLIGHT_ATTR}]`).forEach(el => unhighlight(el as HTMLElement));
}

function parse(e: React.DragEvent): DragPayload | null {
  try {
    const raw = e.dataTransfer.getData(MIME);
    return raw ? (JSON.parse(raw) as DragPayload) : null;
  } catch {
    return null;
  }
}

/** Builds the DnD handler bag. Call once (the board) and share via the provider. */
export function useStickyboardDndValue(): StickyboardDnd {
  const patch = usePatchSticky();
  const createStack = useCreateStack();
  const updateStack = useUpdateStack();
  const { suggest } = useSuggestStackName();

  const { notify } = useStewardNotifier();
  const sectionsData = useSections().data;
  const stacksData = useStacks().data;

  const createStackAsync = createStack.mutateAsync;
  const renameStack = updateStack.mutate;
  const sectionLabels = useMemo(
    () => new Map((sectionsData ?? []).map(s => [s.section_id, s.label] as const)),
    [sectionsData]
  );
  const stackLabels = useMemo(
    () => new Map((stacksData ?? []).map(s => [s.stack_id, s.label] as const)),
    [stacksData]
  );

  return useMemo<StickyboardDnd>(() => {
    const move = patch.mutate;
    // Human labels for the "user-updated:" note (empty section id = Unsorted).
    const sectionName = (id: string) => (id === '' ? 'Unsorted' : (sectionLabels.get(id) ?? id));
    const stackName = (id: string) => stackLabels.get(id) ?? id;

    // Two single cards dropped together → make a stack, then ask the Steward for
    // a name (replacing the placeholder). Optimistic: the pile forms instantly;
    // the name resolves a moment later (or stays "New stack" on failure).
    const stackByDrop = async (dragged: CardDrag, target: StickyCard) => {
      const section = effectiveSectionId(target) ?? '';
      let stackId: string;
      try {
        const stack = await createStackAsync({ label: 'New stack' });
        stackId = stack.stack_id;
      } catch {
        return; // toast already surfaced by the mutation
      }
      const payload = { user_stack_override: stackId, user_section_id_override: section };
      move({ stickyId: dragged.stickyId, payload });
      move({ stickyId: target.sticky_id, payload });
      notify(`stacked "${dragged.title}" with "${target.title}"`);
      const name = await suggest([
        { id: dragged.stickyId, title: dragged.title },
        { id: target.sticky_id, title: target.title },
      ]);
      if (name) renameStack({ stackId, patch: { label: name } });
    };

    return {
      startCardDrag: (card: StickyCard) => e => {
        const payload: CardDrag = {
          kind: 'card',
          stickyId: card.sticky_id,
          fromSection: effectiveSectionId(card) ?? '',
          fromStack: effectiveStackId(card) ?? '',
          title: card.title,
        };
        e.dataTransfer.setData(MIME, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = 'move';
        // Don't let an expanded pile's root onDragStart overwrite this card
        // payload with the stack payload as the event bubbles.
        e.stopPropagation();
      },

      startStackDrag: (stackId, section, members) => e => {
        const payload: StackDrag = {
          kind: 'stack',
          stackId,
          section,
          memberIds: members.map(m => m.sticky_id),
        };
        e.dataTransfer.setData(MIME, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = 'move';
        // The collapsed pile clips its (always-mounted) expanded cards via
        // overflow:hidden, but Chrome's DEFAULT drag image ignores that clip and
        // snapshots the full, tall content. Give it an explicit ghost = an
        // off-screen clone of just the collapsed pile.
        const el = e.currentTarget as HTMLElement;
        const r = el.getBoundingClientRect();
        const ghost = el.cloneNode(true) as HTMLElement;
        ghost.style.position = 'fixed';
        ghost.style.top = '-10000px';
        ghost.style.left = '0';
        ghost.style.width = `${r.width}px`;
        ghost.style.margin = '0';
        ghost.style.pointerEvents = 'none';
        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, e.clientX - r.left, e.clientY - r.top);
        // Remove after the browser has snapshotted it (next tick).
        setTimeout(() => ghost.remove(), 0);
        e.stopPropagation();
      },

      allowDrop: e => {
        e.preventDefault();
        // Only the innermost zone under the pointer highlights (don't let the
        // dragover bubble up and also light the lane behind a card).
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        clearHighlights();
        highlight(e.currentTarget as HTMLElement);
      },

      leaveDrop: e => unhighlight(e.currentTarget as HTMLElement),

      endDrag: () => clearHighlights(),

      dropOnSection: (sectionId: string) => e => {
        e.preventDefault();
        e.stopPropagation();
        clearHighlights();
        const p = parse(e);
        if (!p) return;
        if (p.kind === 'card') {
          // Dropped into open lane space: re-section AND pull out of any stack.
          // No-op if it's already an ungrouped card in this lane.
          if (p.fromSection === sectionId && p.fromStack === '') return;
          move({
            stickyId: p.stickyId,
            payload: { user_section_id_override: sectionId, user_stack_override: '' },
          });
          notify(
            p.fromStack
              ? `removed "${p.title}" from its stack and moved it to ${sectionName(sectionId)}`
              : `moved "${p.title}" from ${sectionName(p.fromSection)} to ${sectionName(sectionId)}`
          );
        } else {
          // Whole stack → new lane: re-section every member (keep the stack).
          if (p.section === sectionId) return;
          for (const id of p.memberIds) {
            move({ stickyId: id, payload: { user_section_id_override: sectionId } });
          }
          notify(`moved stack "${stackName(p.stackId)}" to ${sectionName(sectionId)}`);
        }
      },

      dropOnStack: (stackId: string, section: string) => e => {
        e.preventDefault();
        e.stopPropagation();
        clearHighlights();
        const p = parse(e);
        if (!p || p.kind !== 'card') return; // dropping a stack on a stack: ignore
        if (p.fromStack === stackId) return; // already a member
        move({
          stickyId: p.stickyId,
          payload: { user_stack_override: stackId, user_section_id_override: section },
        });
        notify(`added "${p.title}" to stack "${stackName(stackId)}"`);
      },

      dropOnCard: (target: StickyCard) => async e => {
        e.preventDefault();
        e.stopPropagation();
        clearHighlights();
        const p = parse(e);
        if (!p || p.kind !== 'card' || p.stickyId === target.sticky_id) return;
        const targetStack = effectiveStackId(target) ?? '';
        if (targetStack) {
          // The target already belongs to a stack → just join it.
          if (p.fromStack === targetStack) return;
          move({
            stickyId: p.stickyId,
            payload: {
              user_stack_override: targetStack,
              user_section_id_override: effectiveSectionId(target) ?? '',
            },
          });
          notify(`added "${p.title}" to stack "${stackName(targetStack)}"`);
          return;
        }
        // Two singles → form a new stack (the Steward names it).
        await stackByDrop(p, target);
      },
    };
  }, [patch.mutate, createStackAsync, renameStack, suggest, notify, sectionLabels, stackLabels]);
}

export function StickyboardDndProvider({ children }: { children: ReactNode }) {
  const value = useStickyboardDndValue();
  return <StickyboardDndContext.Provider value={value}>{children}</StickyboardDndContext.Provider>;
}
